import { defineComponent as _defineComponent } from 'vue'
import WizardStep from '@/components/wizard/WizardStep.vue';
import { User, UserAuthenticationType } from '@/models/user';
import { computed, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import {
  email,
  maxLength,
  or,
  required,
  requiredIf,
  sameAs,
  withLocalizedValidators,
  passwordsMatch,
  optional,
} from '@/utils/validation';
import { usersService } from '@/services/users.service';
import { useI18n } from '@/composables/i18n';


/** @deprecated THIS COMPONENT IS FROZEN
 *
 * User creation wizard step to enter data like name, email, password...
 */
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'PersonalDataWizardStep',
  props: {
    value: null,
    isEdit: { type: Boolean, default: false }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ((e: 'input', user: User) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { value: User, isEdit: boolean };





const { t } = useI18n();

const repeatPassword = ref('');
const authenticationTypes = [
  UserAuthenticationType.Local,
  UserAuthenticationType.Microsoft,
];

const validations = {
  name: withLocalizedValidators('user.name', { required }),
  email: withLocalizedValidators('user.email', { required, email }),
  authenticationType: withLocalizedValidators(
    'user_authentication_type.label',
    {
      required,
      oneOf: or(
        sameAs(UserAuthenticationType.Microsoft),
        sameAs(UserAuthenticationType.Local),
      ),
    },
  ),
  password: withLocalizedValidators('user.password', {
    required: requiredIf(
      () =>
        props.value.authenticationType === UserAuthenticationType.Local &&
        !props.isEdit,
    ),
    // These validation rules only needs to pass if the password is required
    maxLength: optional(maxLength(256)),
    password_repeat: optional(passwordsMatch(repeatPassword)),
  }),
};
const $externalResults = ref({});
const v$ = useVuelidate(validations, props.value, { $externalResults });

async function beforeLeave() {
  v$.value.$clearExternalResults();
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return false;
  }

  if (props.isEdit) {
    // The email can't be edited, so it'll always exist
    return true;
  }

  const emailExists = await usersService.emailExists(props.value.email);
  if (emailExists) {
    $externalResults.value = {
      email: t('validation_message.email_exists'),
    };
  }
  return !emailExists;
}

function onEmailInput() {
  v$.value.$clearExternalResults();
  onInput();
}

function onInput() {
  emit('input', props.value);
}

function getErrorMessage(key: keyof typeof props.value): string | undefined {
  return v$.value[key].$errors?.[0]?.$message;
}

watch(
  () => props.value.authenticationType,
  (value) => {
    if (value === UserAuthenticationType.Microsoft) {
      props.value.password = undefined;
      repeatPassword.value = '';
    }
  },
);

const passwordRequired = computed(
  () => props.value.authenticationType === UserAuthenticationType.Local,
);

const canChooseAuthenticationType = computed(
  () => !props.value.isServiceUserType && !props.value.isTechnologyUser,
);

const authenticationTypeDisabledReason = computed(() => {
  const prefix = 'wizards.user_creation.personal_data.user_authentication_type';

  if (props.isEdit) {
    return t(`${prefix}.disabled_because_editing`);
  }

  if (!canChooseAuthenticationType.value) {
    return t(`${prefix}.disabled_because_of_user_type`);
  }

  return undefined;
});

return { __sfc: true,props, emit, t, repeatPassword, authenticationTypes, validations, $externalResults, v$, beforeLeave, onEmailInput, onInput, getErrorMessage, passwordRequired, canChooseAuthenticationType, authenticationTypeDisabledReason, WizardStep, email }
}

})